import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, FormHelperText, Grid, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import OtpInput from '../OtpInput/OtpInput';
import { isInteger } from '../../utils/utils';
import { SIGNUP_ROUTE } from '../../utils/routes';
import { onboardingActions } from '../../redux/onboarding/onboardingSlice';
import { brandTagKey } from '../../config/constants';
import { logout } from '../../redux/GlobalAction';

const StyledTextButton = styled(Button)({
  fontWeight: 700,
  fontSize: '17px !important',
  padding: '0px 10px !important',
  background: 'linear-gradient(137.65deg, #6599A5 24.11%, #3B667E 83.2%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
});

const VERIFICATION_CODE_LENGTH = 6;

const EmailVerification = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [verificationCode, setVerificationCode] = useState('');
  const { email, id } = useAppSelector((state) => state.onboarding.therapist);
  const cEmail = useAppSelector((state) => state.coach.coach.email);
  const cId = useAppSelector((state) => state.coach.coach.id);
  const isValid = !useAppSelector((state) => state.onboarding.verifyEmailError.hasError);

  useEffect(() => {
    dispatch(onboardingActions.verifyEmailFailureReset());
  }, []);

  const onVerificationCodeEnter = (code: string) => {
    if (!code || code.length !== VERIFICATION_CODE_LENGTH) return;
    dispatch(
      onboardingActions.verifyEmail({
        data: { coachId: id || cId, verificationCode: code },
        navigate,
      })
    );
  };

  const onVerificationCodeChange = (code: string) => {
    setVerificationCode(code);
    dispatch(onboardingActions.verifyEmailFailureReset());
  };

  const [seconds, setSeconds] = useState(0);

  const formatTime = (time: number) => (time < 10 ? `0${time}` : time);

  const countdownRef = useRef<ReturnType<typeof setInterval> | number | null>(null);

  useEffect(() => {
    if (seconds === 20) {
      countdownRef.current = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds - 1);
      }, 1000);
    }

    if (seconds === 0 && countdownRef.current) {
      clearInterval(countdownRef.current);
    }
  }, [seconds]);

  const handleResendResetCode = () => {
    if (!cId && !id) return;
    dispatch(onboardingActions.resendVerificationCode({ coachId: id || cId }));
    setSeconds(20);
  };

  const handleWrongEmail = () => {
    dispatch(logout());
    const brandTag = localStorage.getItem(brandTagKey);
    navigate(brandTag ? `${SIGNUP_ROUTE}/${brandTag}` : SIGNUP_ROUTE);
  };

  return (
    <Grid container textAlign="center" maxWidth={373} spacing={6} px={{ xs: 2 }}>
      <Grid item container gap="11px" flexDirection="column">
        <Box component="img" src="/assets/icons/letter.svg" height={56} />
        <Typography
          variant="title2Bold"
          sx={{
            background: 'linear-gradient(137.65deg, #6599A5 24.11%, #3B667E 83.2%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {t('therapistOnboarding.emailVerification.title')}
        </Typography>
      </Grid>

      <Grid item container gap={6}>
        <Typography variant="bodyRegular" color="primary">
          {t('therapistOnboarding.emailVerification.sendTo')}{' '}
          <Typography variant="bodyBold" color="primary">
            {`${email || cEmail}.`}
          </Typography>
          {t('therapistOnboarding.emailVerification.description')}
        </Typography>

        <Box display="flex" flexDirection="column" gap="4px">
          <OtpInput
            autoFocus
            isValid={isValid}
            length={VERIFICATION_CODE_LENGTH}
            value={verificationCode}
            validateChar={isInteger}
            onChange={onVerificationCodeChange}
            onComplete={onVerificationCodeEnter}
            gap="4px"
          />
          {!isValid && (
            <FormHelperText style={{ color: 'red', textAlign: 'center' }}>
              {t('therapistOnboarding.emailVerification.invalidCode')}
            </FormHelperText>
          )}
        </Box>

        <Grid container justifyContent="center" gap={1.5}>
          {seconds > 0 ? (
            <Typography variant="bodyBold" color="primary">
              {`${t('therapistOnboarding.emailVerification.resendCode')} (${t(
                'therapistOnboarding.emailVerification.wait'
              )} ${formatTime(seconds % 60)}s)`}
            </Typography>
          ) : (
            <StyledTextButton variant="text" onClick={handleResendResetCode}>
              {`${t('therapistOnboarding.emailVerification.resendCode')} ${t(
                'therapistOnboarding.emailVerification.resend'
              )}`}
            </StyledTextButton>
          )}
          <StyledTextButton variant="text" onClick={handleWrongEmail}>
            {`${t('therapistOnboarding.emailVerification.wrongEmail')}`}{' '}
          </StyledTextButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EmailVerification;
